import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Testids } from '@hweeks/kei-costs-ui-testids';
import { AudienceCard, ButtonLink, Container, Screenshot, SectionLabel } from '../../design';
import { pagePricing, priced, pricingText } from '../../lib/pricing';
import { AnalyticsEvent, ctaEvent } from '../../utils/analytics';
import { CtaBand } from './CtaBand';
import { CarrierShip, Spot, StampedSheet } from './illustrations';
import { JobsCarousel } from './JobsCarousel';
import { Journey } from './Journey';
import { MKT } from './links';
import { MarketingLayout } from './MarketingLayout';
import { CROPS, SHOTS, SHOTS_NOTE } from './shots';
import { TrustGrid } from './TrustGrid';
import './home.css';
// The logged-out homepage. It speaks to both kinds of customer: the hero splits them — a
// dealership, or someone importing their own car — and sends each to its own page; the
// journey, the real screens and the trust grid below hold for both.
// Each job's excerpt: the cropped screen that proves it, in the theme opposite the page's.
const excerpt = (shot)=>/*#__PURE__*/ _jsx(Screenshot, {
        ...shot,
        bare: true,
        invert: true
    });
const JOBS = [
    {
        k: '検索',
        title: 'Search every auction at once',
        text: 'Search once across the agent accounts you already have. Save it as a hunt and you’ll hear when a new match turns up.',
        href: MKT.product('search'),
        excerpt: excerpt(CROPS.search)
    },
    {
        k: '査定',
        title: 'Read any auction sheet',
        text: 'The grade, mileage, damage map, options, inspector notes and full frame number, translated into plain English.',
        href: MKT.product('auction-sheets'),
        excerpt: excerpt(CROPS.sheet)
    },
    {
        k: '相場',
        title: 'Know what it’ll go for',
        text: 'A bid range based on what similar lots sold for at auction.',
        href: MKT.product('pricing-guidance'),
        excerpt: excerpt(CROPS.bid)
    },
    {
        k: '探索',
        title: 'Hunts for your shop or your customers',
        text: 'Searches that keep running. They read and price each lot, chase the sheets that are missing and draft the customer shortlist in your own mailbox.',
        href: MKT.product('hunts'),
        excerpt: excerpt(CROPS.hunt)
    },
    {
        k: '入札',
        title: 'Watch, bid, won. One tap each.',
        text: 'Any lot you act on turns into a single car record. When the auction ends, the app checks the result and asks you to confirm it.',
        href: MKT.product('bidding'),
        excerpt: excerpt(CROPS.stages)
    },
    {
        k: '原価',
        title: 'Your real landed cost',
        text: 'Drop in the invoices and receipts. The app pulls out each line for you to check, totals it per car and links it to the document it came from.',
        href: MKT.product('landed-cost'),
        excerpt: excerpt(CROPS.costs)
    },
    {
        k: '売却',
        title: 'Profit per car',
        text: 'Record the sale and see what that car really made you, worked out from what it cost instead of a spreadsheet’s guess.',
        href: MKT.product('landed-cost'),
        // The app draws this figures band in the opposite tone to its own theme (an ink band on a
        // light screen), so the page's own theme is what stands off the page here.
        excerpt: /*#__PURE__*/ _jsx(Screenshot, {
            ...CROPS.profit,
            bare: true
        })
    }
];
export default function HomePage() {
    const t = pricingText(pagePricing());
    return /*#__PURE__*/ _jsx(MarketingLayout, {
        testid: Testids.app.landingPage.root,
        children: /*#__PURE__*/ _jsxs("main", {
            children: [
                /*#__PURE__*/ _jsxs("section", {
                    className: "mk-home__hero",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            "aria-hidden": "true",
                            className: "mk-home__hero-mark",
                            children: "車"
                        }),
                        /*#__PURE__*/ _jsxs(Container, {
                            className: "mk-home__hero-inner",
                            children: [
                                /*#__PURE__*/ _jsx(SectionLabel, {
                                    k: "右",
                                    className: "mk-home__hero-label",
                                    children: "Right Hand DMS · JDM imports from auction"
                                }),
                                /*#__PURE__*/ _jsx("h1", {
                                    className: "mk-home__hero-title",
                                    children: "Cars from Japanese auctions, brought home with a plan."
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mk-home__hero-lead",
                                    children: "JDM import software that covers the search, the auction sheet, the bid, customs, the truck home and every receipt. Use it to run a dealership, or to bring in your first Japanese car."
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "mk-home__audiences",
                                    children: [
                                        /*#__PURE__*/ _jsx(AudienceCard, {
                                            k: "商",
                                            who: "For dealerships",
                                            title: "I run a dealership",
                                            promise: "Source cars for every customer and for your own lot. Your team, your exporter accounts and your mailbox sit in one system.",
                                            points: [
                                                'A hunt per customer, with match alerts and weekly digests you approve',
                                                'Bid ranges from sold lots, then landed cost and profit per car',
                                                'Seats and roles for the whole team'
                                            ],
                                            action: /*#__PURE__*/ _jsx(ButtonLink, {
                                                href: MKT.forDealers,
                                                "data-testid": Testids.app.landingPage.audienceCta('dealer'),
                                                ...ctaEvent(AnalyticsEvent.ctaAudience, 'home-dealer'),
                                                children: "See it for dealers"
                                            }),
                                            aside: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    "Plans from ",
                                                    /*#__PURE__*/ _jsx("span", {
                                                        ...priced('from'),
                                                        children: t.from
                                                    }),
                                                    " a month"
                                                ]
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(AudienceCard, {
                                            k: "個",
                                            who: "For individuals",
                                            title: "I’m importing my own",
                                            promise: "Bring in one JDM car or truck for yourself. The app takes you through each step, forms and emails included.",
                                            points: [
                                                /*#__PURE__*/ _jsxs(_Fragment, {
                                                    children: [
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pass.hunts'),
                                                            children: t['pass.hunts']
                                                        }),
                                                        " watching the auctions for you, included for ",
                                                        /*#__PURE__*/ _jsx("span", {
                                                            ...priced('pass.days'),
                                                            children: t['pass.days']
                                                        }),
                                                        ' ',
                                                        "days"
                                                    ]
                                                }),
                                                'Auction grades explained, and a bid range for each lot',
                                                'Customs, shipping and paperwork, one step at a time'
                                            ],
                                            action: /*#__PURE__*/ _jsx(ButtonLink, {
                                                href: MKT.importMyOwn,
                                                "data-testid": Testids.app.landingPage.audienceCta('individual'),
                                                ...ctaEvent(AnalyticsEvent.ctaAudience, 'home-individual'),
                                                children: "See how it works for you"
                                            }),
                                            aside: /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        ...priced('pass.price'),
                                                        children: t['pass.price']
                                                    }),
                                                    ", your first car included"
                                                ]
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 260,
                    children: /*#__PURE__*/ _jsx(StampedSheet, {})
                }),
                /*#__PURE__*/ _jsx("section", {
                    id: "how",
                    className: "mk-home__how mk-ground--kinari",
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "手順",
                                className: "mk-home__label-mb16",
                                children: "How it works"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-home__h2",
                                children: "Five stops from an auction in Japan to your door."
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-home__sub",
                                children: "A dealer’s fortieth car takes the same path as a first-timer’s Skyline, Hiace or Land Cruiser. The app keeps track of where each car is on it and what comes next."
                            }),
                            /*#__PURE__*/ _jsx(Journey, {
                                testid: Testids.app.landingPage.how
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Spot, {
                    width: 300,
                    children: /*#__PURE__*/ _jsx(CarrierShip, {})
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-home__shots mk-ground--sora",
                    "data-testid": Testids.app.landingPage.shots,
                    children: /*#__PURE__*/ _jsxs(Container, {
                        children: [
                            /*#__PURE__*/ _jsx(SectionLabel, {
                                k: "画面",
                                className: "mk-home__label-mb16",
                                children: "The real app"
                            }),
                            /*#__PURE__*/ _jsx("h2", {
                                className: "mk-home__h2",
                                children: "What it looks like on an ordinary day."
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "mk-home__sub",
                                children: SHOTS_NOTE
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-home__shots-grid",
                                children: [
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...SHOTS.hunt,
                                        invert: true,
                                        className: "mk-home__shot-main",
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "A hunt’s results."
                                                }),
                                                " Lots from each of your exporter accounts, every one read and scored against what you asked for."
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.costs,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "One car’s landed cost."
                                                }),
                                                " Each cost line in dollars, with the total at the bottom."
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(Screenshot, {
                                        ...CROPS.today,
                                        bare: true,
                                        invert: true,
                                        caption: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx("b", {
                                                    children: "Today."
                                                }),
                                                " The decisions waiting on you, and how each hunt is going."
                                            ]
                                        })
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(TrustGrid, {}),
                /*#__PURE__*/ _jsx(JobsCarousel, {
                    title: "Seven jobs, one system, from the auction to the sale.",
                    jobs: JOBS
                }),
                /*#__PURE__*/ _jsx("section", {
                    className: "mk-home__pricing-teaser-section",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "mk-home__pricing-teaser-div",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-home__teaser-col",
                                children: [
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "mk-home__pricing-teaser-h3",
                                        children: [
                                            "Dealership plans from ",
                                            /*#__PURE__*/ _jsx("span", {
                                                ...priced('from'),
                                                children: t.from
                                            }),
                                            " a month"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("p", {
                                        className: "mk-home__pricing-teaser-p",
                                        children: "Priced by the auction sheets you read. Unlimited cars, documents and landed cost."
                                    }),
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.pricingFor('dealer'),
                                        className: "mk-home__pricing-teaser-a",
                                        children: "Dealership plans →"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "mk-home__teaser-col",
                                children: [
                                    /*#__PURE__*/ _jsxs("h2", {
                                        className: "mk-home__pricing-teaser-h3",
                                        children: [
                                            "Import my own: ",
                                            /*#__PURE__*/ _jsx("span", {
                                                ...priced('pass.price'),
                                                children: t['pass.price']
                                            }),
                                            ", first car included"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mk-home__pricing-teaser-p",
                                        children: [
                                            "Searching is included for ",
                                            /*#__PURE__*/ _jsx("span", {
                                                ...priced('pass.days'),
                                                children: t['pass.days']
                                            }),
                                            ' ',
                                            "days from sign-up, and again from each car pass. If you haven’t won a car, we refund your sign-up minus what you used. We suggest giving it",
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                ...priced('guarantee.days'),
                                                children: t['guarantee.days']
                                            }),
                                            " days."
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("a", {
                                        href: MKT.pricingFor('individual'),
                                        className: "mk-home__pricing-teaser-a",
                                        children: "How Import my own is priced →"
                                    })
                                ]
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(CtaBand, {})
            ]
        })
    });
}
